import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";

/* ── Types ────────────────────────────────────────────────────────── */

type TemplateError = {
  idx: number;
  message: string;
};

/* ── Main component ───────────────────────────────────────────────── */

interface ConfigureTemplateProps {
  draftId: number;
  onNext: (templateIds: number[]) => Promise<TemplateError | null>;
  initialTemplateIds?: number[] | null;
}

export default function ConfigureTemplate({
  draftId: _draftId,
  onNext,
  initialTemplateIds,
}: ConfigureTemplateProps) {
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    fetchOutreachTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
  } = useContext(OutreachTemplatesContext);
  const { emailSettings } = useContext(OrganizationUserContext);

  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template: { templateId: number }) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(1);

  /* ── Initialize templates ── */

  useEffect(() => {
    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    // Already initialized — don't overwrite user selections
    if (initialized) return;

    // If we have saved template IDs from the draft, restore them
    if (initialTemplateIds && initialTemplateIds.length > 0) {
      const matchedTemplates: Template[] = [];
      for (const tid of initialTemplateIds) {
        const template = allTemplates?.find((x: Template) => x.id === tid);
        if (template) {
          matchedTemplates.push(template);
        }
      }
      if (matchedTemplates.length > 0) {
        setModifiedTemplates(matchedTemplates);
        setInitialized(true);
        return;
      }
    }

    // Fallback to first outreach template
    if (filteredOutreachTemplates?.length > 0) {
      setModifiedTemplates([filteredOutreachTemplates[0]]);
      setInitialized(true);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length, initialTemplateIds]);

  /* ── Handlers ── */

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x: Template) => x.id === value);
    if (template) {
      copy[index] = template;
      setModifiedTemplates(copy);
      setTemplatesError(null);
    }
  };

  const addFollowUp = () => {
    if (filteredFollowUps?.[0]) {
      setModifiedTemplates([...modifiedTemplates, filteredFollowUps[0]]);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
  };

  const handleGenerateTemplate = () => {
    setCreateTemplateDialogOpen(true);
    setCurrentTemplateContentType(TemplateContentType.OUTREACH);
  };

  const isFormValid =
    modifiedTemplates.length > 0 && !!modifiedTemplates[0]?.id;

  const handleNext = async () => {
    if (!isFormValid) return;
    setLoading(true);
    setTemplatesError(null);
    try {
      const templateIds = modifiedTemplates
        .map((t) => t.id)
        .filter((id): id is number => id !== undefined);
      const error = await onNext(templateIds);
      if (error) {
        setTemplatesError(error);
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  };

  if (!allTemplates || allTemplates.length === 0) {
    return (
      <Box sx={{ maxWidth: 976, mx: "auto" }}>
        <Skeleton
          variant="rounded"
          height={48}
          sx={{ borderRadius: "10px", mb: 2 }}
        />
        <Skeleton
          variant="rounded"
          height={320}
          sx={{ borderRadius: "14px" }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 976, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: "purple.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="span"
            className="fa-regular fa-envelope"
            sx={{ fontSize: 18, color: "white" }}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 24,
              color: "text.primary",
              lineHeight: "32px",
            }}
          >
            Email Templates
          </Typography>
          <Typography
            sx={{ fontSize: 16, color: "text.secondary", lineHeight: "24px" }}
          >
            Choose the outreach and follow-up templates for this automation
          </Typography>
        </Box>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* ── Left panel: form card ── */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            p: 3,
          }}
        >
          {/* Outreach Template */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}
            >
              <Typography
                sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}
              >
                Outreach Template
                <Typography
                  component="span"
                  sx={{ color: "error.main", ml: 0.5, fontSize: 14 }}
                >
                  *
                </Typography>
              </Typography>
            </Box>
            {modifiedTemplates[0] ? (
              <OutreachSelect
                templates={modifiedTemplates}
                handleChange={handleTemplateChange}
                filteredOutreachTemplates={filteredOutreachTemplates}
                templateFromEmailSettings={templateFromEmailSettings}
                addNewOption={handleGenerateTemplate}
                templatesError={templatesError}
              />
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={handleGenerateTemplate}
                disableElevation
                sx={{
                  height: 32,
                  textTransform: "none",
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "#01332b" },
                }}
              >
                Generate Template
              </Button>
            )}
          </Box>

          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              my: 2,
            }}
          >
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
          </Box>

          {/* Follow-up Templates */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}
            >
              <Typography
                sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}
              >
                Follow-up Templates
              </Typography>
              <Typography
                sx={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}
              >
                optional
              </Typography>
            </Box>

            {selectedFollowups?.map((followup, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Grid container alignItems="center" gap={1}>
                  <Grid item xs>
                    <Select
                      onChange={(e) => {
                        e.stopPropagation();
                        handleTemplateChange(e, index + 1);
                      }}
                      value={followup?.id}
                      size="small"
                      fullWidth
                      sx={{
                        borderRadius: "10px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e7eb",
                          borderWidth: 2,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      {filteredFollowUps?.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Typography noWrap sx={{ maxWidth: 300 }}>
                              {template.name}
                            </Typography>
                            {template.id &&
                              templateFromEmailSettings?.includes(
                                template.id,
                              ) && (
                                <Chip
                                  label="Last Used"
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    fontSize: 10,
                                    backgroundColor: "primary.light",
                                  }}
                                />
                              )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  {followup?.id && (
                    <Grid item xs="auto">
                      <TemplateEditButton templateId={followup.id} />
                    </Grid>
                  )}

                  <Grid item xs="auto">
                    <IconButton
                      sx={{ fontSize: 13, color: "#9ca3af" }}
                      onClick={() => removeFollowUp(index + 1)}
                    >
                      <Box component="i" className="fa-regular fa-trash" />
                    </IconButton>
                  </Grid>
                </Grid>

                {templatesError && templatesError.idx === index + 1 && (
                  <Typography
                    sx={{ mt: 0.5, color: "warning.main", fontSize: 13 }}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-warning"
                      sx={{ mr: 0.5 }}
                    />
                    {templatesError.message}
                  </Typography>
                )}

                <Typography
                  sx={{
                    fontSize: 11,
                    fontStyle: "italic",
                    color: "#9ca3af",
                    mt: 0.5,
                  }}
                >
                  Sent 4 business days after previous email
                </Typography>
              </Box>
            ))}

            {filteredFollowUps?.length > 0 &&
              modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                <Button
                  onClick={addFollowUp}
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: "none",
                    borderColor: "#e5e7eb",
                    color: "primary.main",
                    borderRadius: "10px",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "rgba(1,67,57,0.04)",
                    },
                  }}
                >
                  + Add Follow-up
                </Button>
              )}

            {filteredFollowUps?.length === 0 && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setCreateTemplateDialogOpen(true);
                  setCurrentTemplateContentType(TemplateContentType.FOLLOWUP);
                }}
                disableElevation
                sx={{
                  height: 32,
                  textTransform: "none",
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "#01332b" },
                }}
              >
                Generate Follow-up
              </Button>
            )}
          </Box>

          {/* Next button */}
          <LoadingButton
            variant="contained"
            fullWidth
            disabled={!isFormValid}
            loading={loading}
            onClick={handleNext}
            sx={{
              mt: 3,
              borderRadius: "10px",
              backgroundColor: "primary.main",
              fontWeight: 600,
              fontSize: 16,
              py: 1.5,
              textTransform: "none",
            }}
          >
            Next
          </LoadingButton>
        </Box>

        {/* ── Right panel: tips & info ── */}
        <Box
          sx={{
            width: 310,
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Tips card */}
          <Box
            sx={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fee685",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}
            >
              <Box
                component="span"
                className="fa-solid fa-lightbulb"
                sx={{ fontSize: 18, color: "#7b3306" }}
              />
              <Typography
                sx={{ fontWeight: 600, fontSize: 18, color: "#7b3306" }}
              >
                Tips
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {[
                "Choose an outreach template as your first email",
                "Add up to 3 follow-ups to increase response rates",
                "Follow-ups are sent 4 business days apart automatically",
                "Use {First Name} and {Brand Name} as personalization tags",
              ].map((tip, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    color: "#e17100",
                    mb: 1,
                    "&::marker": { color: "#e17100" },
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, color: "#7b3306", lineHeight: "20px" }}
                  >
                    {tip}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* How it works card */}
          <Box
            sx={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#166534", mb: 1 }}
            >
              Email sequence
            </Typography>
            <Box sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}>
              <Box sx={{ display: "flex", gap: 1, mb: 0.75 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: "#dcfce7",
                    color: "#166534",
                    borderRadius: "4px",
                    px: 0.75,
                    py: 0.25,
                    lineHeight: "18px",
                    flexShrink: 0,
                  }}
                >
                  1st
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#15803d" }}>
                  Outreach email sent to new contacts
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 0.75 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: "#dcfce7",
                    color: "#166534",
                    borderRadius: "4px",
                    px: 0.75,
                    py: 0.25,
                    lineHeight: "18px",
                    flexShrink: 0,
                  }}
                >
                  2nd
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#15803d" }}>
                  Follow-up if no reply after 4 business days
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: "#dcfce7",
                    color: "#166534",
                    borderRadius: "4px",
                    px: 0.75,
                    py: 0.25,
                    lineHeight: "18px",
                    flexShrink: 0,
                  }}
                >
                  3rd+
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#15803d" }}>
                  Additional follow-ups continue the cadence
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
